import { BCMSDefaultModel, BCMSRecord, Link, ModalOpen, useWorkflowStep } from "@bcm-dev/bcms-vite";
import BillerGroupsAccordion, { BillerGroupsButton, BillerGroupsButtonContainer } from "compid/custom-ui/CustomAccordion/components/BillerGroupsAccordion";
import PageLayoutContainer from "compid/shared/containers/PageLayoutContainer";
import CenterColumnContainer from "compid/shared/containers/PageLayoutContainer/containers/CenterColumnContainer";
import PageView from "compid/shared/containers/PageViewContainer";
import GroupBillerView from "compid/shared/views/BillerView/components/views/GroupBillerView";
import InfoNotificationView from "compid/shared/views/Notifications/InfoNotificationView";
import SuccessNotificationView from "compid/shared/views/Notifications/SuccessNotificationView";
import UIButton from "compid/ui/UIButton";
import UITextInput from "compid/ui/UITextInput";
import UIToggle from "compid/ui/UIToggle";
import { getDefault, getWorkflowSteps } from "compid/workflows/BillerGroupsWorkflow";
import BillerGroupEditItem from "compid/workflows/BillerGroupsWorkflow/classes/BillerGroupEditItem";
import { Biller, BillerGroup } from "model";
import React, { useEffect, useState } from "react";
import { SVGAsset } from "res";
import "./styles.css";

/**
 * @function BillerGroupsPage
 * @param {STRING} addedBillerGroupName - The name of the added biller.
 * @param {STRING} billerGroupName - This sets the existing biller group name.
 * @returns {React.Component}
 * @exported true
 * @description This is the main component for the BillerGroupsPage. This page allows you to manage existing biller groups and add new biller groups.
 * @example
 * <BillerGroupsPage />
 */
export default function BillerGroupsPage(props) {
  const workflowStep = useWorkflowStep(getDefault, getWorkflowSteps);
  BCMSRecord.globalControllerFor([ Biller, BillerGroup ], workflowStep.globalController);
  const billerGroups = workflowStep.read("billerGroups");
  const addedNewBillerGroup = workflowStep.read("addedNewBillerGroup");
  const newBillerGroup = workflowStep.read("newBillerGroup");
  const addedNewBillerGroupName = workflowStep.read("addedNewBillerGroupName");
  const didMoveBillerToBillerGroup = workflowStep.read("didMoveBillerToBillerGroup");
  const movedBiller = workflowStep.read("movedBiller");
  const movedBillerFromBillerGroup = workflowStep.read("movedBillerFromBillerGroup");
  const movedBillerToBillerGroup = workflowStep.read("movedBillerToBillerGroup");
  const deletedBillerGroup = workflowStep.read("deletedBillerGroup");
  const deletedBillerGroupName = workflowStep.read("deletedBillerGroupName");
  const billerGroupEditItems = workflowStep.read("billerGroupEditItems");
  const didEditBillerGroupName = workflowStep.read("didEditBillerGroupName");
  const editedBillerGroupName = workflowStep.read("editedBillerGroupName");
  const didEditBillerGroup = workflowStep.read("didEditBillerGroup");
  const currBillerGroupEditItem = workflowStep.read("currBillerGroupEditItem");

  // When "updateWorkflow" is called, the workflow state is updated.
  // Temporary solution since "BCMSRecord.batch" will occasionally 
  // set the workflow state to an older state.
  const [updateWorkflow, onUpdateWorkflow] = useState(undefined);

  // Hides all banners in a single call.
  const hideBanners = () => {
    workflowStep.update(["didMoveBillerToBillerGroup"], false);
    workflowStep.update(["deletedBillerGroup"], false);
    workflowStep.update(["addedNewBillerGroup"], false);
    workflowStep.update(["didEditBillerGroup"], false);
  }

  const onBillerGroupItemClick = obj => {
    const selectedBillerGroup = obj.billerGroup;
    const selectedBiller = obj.biller;

    // Update the array of biller groups in the workflow state.
    var _billerGroups = billerGroups;

    // Filter out this biller if its already in an existing biller group.
    _billerGroups = _billerGroups.map(group => {
      if(group.billers) {
        const billers = group.billers.filter(biller => {
          return biller.id !== selectedBiller.id;
        });
        group.billers = billers;
      }
      return group;
    });

    var prevBillerGroup = selectedBiller.billerGroup;

    // For a selected biller, update the "billerGroup" property.
    selectedBiller.billerGroup = selectedBillerGroup;

    // If we are moving biller to "Other Billers",
    // set the "billerGroup" property to undefined.
    if(selectedBillerGroup.otherBillers) { selectedBiller.billerGroup = undefined; }

    // Check if any billers exist for this biller group.
    if(selectedBillerGroup.billers) {
      // This group is shown if a biller is added for the first time.
      if(selectedBillerGroup.billers.length === 0) { selectedBillerGroup.show = true; }
      // Move the biller to the top of the array of billers.
      selectedBillerGroup.billers.splice(0, 0, selectedBiller);
    }

    const index = _billerGroups.findIndex(group => group.id === selectedBillerGroup.id);
    _billerGroups.splice(index, 1, selectedBillerGroup);
    BillerGroup.sortNameAscending(selectedBillerGroup.billers);

    // Update the workflow.
    onUpdateWorkflow(() => {
      return () => {
        workflowStep.update(["billerGroups"], _billerGroups);
        workflowStep.update(["movedBiller"], selectedBiller);
        workflowStep.update(["movedBillerFromBillerGroup"], prevBillerGroup);
        workflowStep.update(["movedBillerToBillerGroup"], selectedBiller.billerGroup);
        hideBanners();
        workflowStep.update(["didMoveBillerToBillerGroup"], true);
      };
    });

    const updates = [
      BillerGroup.update(selectedBillerGroup),
      Biller.update(selectedBiller),
    ];
    // Biller groups are hidden if it has no more billers.
    if(prevBillerGroup !== undefined) {
      if(prevBillerGroup.billers.length < 1) {
        prevBillerGroup.show = false;
      }
      updates.push(BillerGroup.update(prevBillerGroup));
    }

    BCMSRecord.batch([
      // Create a new biller group.
      ...updates
    ], workflowStep.globalController);
  }

  // Handle group deletion here. 
  // Currently a group deletion happens through the "Delete"
  // button which opens a confirmation modal.
  const onDeleteButtonClick = obj => {
    const selectedBillerGroup = obj.billerGroup ?? {};
    const selectedBillers = selectedBillerGroup.billers ?? {};
    let updates = [];
    // Update the array of biller groups in the workflow state.
    var _billerGroups = billerGroups;

    // Get the "Other billers" group and we will
    // push the deleted groups billers into this group.
    const otherBillers = _billerGroups.filter(group => { return group.otherBillers === true; })[0];

    // If any billers are in this group, we will move them to the "Other billers" group.
    if(selectedBillers.length > 0) {
      updates = selectedBillers.reduce((acc, biller) => {
        biller.billerGroup = undefined;
        otherBillers.billers.push(biller);
        acc.push(Biller.update(biller));
        return acc;
      }, []);
    }
    updates.push(BillerGroup.delete(selectedBillerGroup.id));
    BillerGroup.sortNameAscending(otherBillers.billers);

    // Filter out the deleted group.
    _billerGroups = _billerGroups.filter(billerGroup => {
      return billerGroup.id !== selectedBillerGroup.id;
    });

    // Update the workflow.
    onUpdateWorkflow(() => {
      return () => {
        workflowStep.update(["billerGroups"], _billerGroups);
        workflowStep.update(["deletedBillerGroupName"], selectedBillerGroup.name);
        hideBanners();
        workflowStep.update(["deletedBillerGroup"], true);
      };
    });

    // Update the model globally.
    BCMSRecord.batch([
      ...updates
    ], workflowStep.globalController);
  }

  const hideGroup = obj => {
    const selectedBillerGroup = obj.billerGroup;
    const show = obj.hideGroup;
    selectedBillerGroup.show = show;

    var _billerGroups = billerGroups;
    const index = _billerGroups.findIndex(group => group.id === selectedBillerGroup.id);
    _billerGroups.splice(index, 1, selectedBillerGroup);

    // Update the workflow.
    onUpdateWorkflow(() => {
      return () => {
        workflowStep.update(["billerGroups"], _billerGroups);
      };
    });

    BCMSRecord.batch([
      // Update a biller group.
      BillerGroup.update(selectedBillerGroup)
    ], workflowStep.globalController);
  };

  // Toggles the "Edit" state of a Biller Group. 
  // Used when clicking the "Edit" and "Cancel" buttons.
  const toggleEditBillerGroupState = obj => {
    const _billerGroup = obj.billerGroup;
    let _billerGroupEditItems = billerGroupEditItems;
    const isEditingBillerGroupItem = _billerGroupEditItems.filter(item => {
       return item.billerGroup.id === _billerGroup.id;
    }).length > 0 ? true : false;
    
    if(isEditingBillerGroupItem) {
      _billerGroupEditItems = _billerGroupEditItems.filter(item => item.billerGroup.id !== _billerGroup.id);
    } else {
      const editItem = new BillerGroupEditItem(_billerGroup);
      _billerGroupEditItems.push(editItem);
    }
    workflowStep.update(["billerGroupEditItems"], _billerGroupEditItems);
  }

  // Update the name of the biller group temporarily. 
  // This change is not saved globally yet at this point.
  // Only the workflow state is updated.
  const updateBillerGroupEditItem = obj => {
    let _billerGroupEditItem = obj.billerGroupEditItem;
    const prop = obj.prop; // "name" is the only prop passed/changed, for now.
    const val = obj.val;
    _billerGroupEditItem.editedBillerGroup[prop] = val;
    const indexOfBillerGroupEditItem = billerGroupEditItems.findIndex(item => item.billerGroup.id === _billerGroupEditItem.billerGroup.id);
    let _billerGroupEditItems = billerGroupEditItems;
    _billerGroupEditItems.splice(indexOfBillerGroupEditItem, 1, _billerGroupEditItem);
    workflowStep.update(["billerGroupEditItems"], _billerGroupEditItems);
  }

  const onConfirmEditButtonClick = obj => {
    const _billerGroupEditItem = obj.billerGroupEditItem;
    const editedBillerGroup = _billerGroupEditItem.editedBillerGroup;

    // If the new group name is an empty string, use the original group name;
    if(editedBillerGroup.name === "") {
      editedBillerGroup.name = _billerGroupEditItem.billerGroup.name;
    }
    const indexOfBillerGroupEditItem = billerGroupEditItems.findIndex(item => item.billerGroup.id === _billerGroupEditItem.billerGroup.id);
    let _billerGroupEditItems = billerGroupEditItems;
    _billerGroupEditItems.splice(indexOfBillerGroupEditItem, 1);

    let _billerGroups = billerGroups;
    const indexOfEditedBillerGroup = _billerGroups.findIndex(group => group.id === _billerGroupEditItem.billerGroup.id);
    _billerGroups.splice(indexOfEditedBillerGroup, 1, editedBillerGroup);

    // Workflow updates.
    onUpdateWorkflow(() => {
      return () => {
        workflowStep.update(["billerGroupEditItems"], _billerGroupEditItems);
        workflowStep.update(["billerGroups"], _billerGroups);
        hideBanners();
        workflowStep.update(["didEditBillerGroup"], true);
        workflowStep.update(["currBillerGroupEditItem"], _billerGroupEditItem);
      };
    });

    // Global updates.
    BCMSRecord.batch([
      BillerGroup.update(editedBillerGroup)
    ], workflowStep.globalController);
  }

  const billerGroupAccordions = billerGroups.reduce((acc, billerGroup, index) => {
    var billers = billerGroup.billers;
    var billerViews = [];
    // If this group has billers, show the billers.
    if(billers) {
      billerViews = billerGroup.billers.reduce((_acc, biller) => {
        _acc.push( <GroupBillerView key={ biller.id } biller={ biller } billerGroups={ billerGroups } onBillerGroupItemClick={ onBillerGroupItemClick } />);
        return _acc;
      }, []);
    }


    if(billerGroup.otherBillers) {
      acc.push(<BillerGroupsAccordion key={ index } text={ billerGroup.name }>{ billerViews }</BillerGroupsAccordion>);
    } else {
      acc.push( 
        <BillerGroupsAccordion key={ index } text={ billerGroup.name }
        billerGroup={ billerGroup } billerGroupEditItems={ billerGroupEditItems } 
        updateBillerGroupEditItem={ updateBillerGroupEditItem }>

          <UserAddedBillerGroupView billerGroups={ billerGroups } billers={ billerGroup.billers } 
          billerGroup={ billerGroup } onBillerGroupItemClick={ onBillerGroupItemClick }
          onDeleteButtonClick={ onDeleteButtonClick } hideGroup={ hideGroup } 
          billerGroupEditItems={ billerGroupEditItems }
          toggleEditBillerGroupState={ toggleEditBillerGroupState }
          onConfirmButtonClick={ onConfirmEditButtonClick } />
          
        </BillerGroupsAccordion>
      );
    }

    return acc;
  }, []);

  // START: Click events
  const billerGroupNameInputVal = val => {
    workflowStep.update(["newBillerGroup", "name"], val);
  }

  const onModalAddGroupClick = () => {
    const defaultBillerGroup = BCMSDefaultModel.billerGroup();
    var updatedNewBillerGroup = BCMSDefaultModel.mergeRecords(newBillerGroup, defaultBillerGroup);
    updatedNewBillerGroup = BillerGroup.init(updatedNewBillerGroup);
    updatedNewBillerGroup.id = BillerGroup.nextID();
    updatedNewBillerGroup.billers = [];
    // "Other billers" is a placeholder group with id of "0" on this page,
    // but no record of it is created.
    // Account for that offset when making a new user group.
    if(updatedNewBillerGroup.id === "0") {
      updatedNewBillerGroup.id = "1";
    }

    // Update the array of biller groups in the workflow state.
    var _billerGroups = billerGroups;
    _billerGroups.splice(0, 0, updatedNewBillerGroup);

    // Set the biller group default.
    const _newBillerGroup = BCMSDefaultModel.billerGroup();
    _newBillerGroup.id = BillerGroup.nextID();
    _newBillerGroup.name = "";

    // Update the workflow.
    onUpdateWorkflow(() => {
      return () => {
        workflowStep.update(["newBillerGroup"], _newBillerGroup);
        workflowStep.update(["billerGroups"], _billerGroups);
        hideBanners();
        workflowStep.update(["addedNewBillerGroup"], true);
        workflowStep.update(["addedNewBillerGroupName"], updatedNewBillerGroup.name);
      }
    })

    BCMSRecord.batch([
      // Create a new biller group.
      BillerGroup.create(updatedNewBillerGroup),
    ], workflowStep.globalController);
  }
  // END: Click events

  // When "updateWorkflow" is called, the workflow state is updated.
  // Temporary solution for action that updates the workflow state
  // and calls "BCMSREcord.batch", since "BCMSRecord.batch" will occasionally 
  // set the workflow state to an older state.
  useEffect(() => {
    if(typeof updateWorkflow === "function") {
      updateWorkflow();
      onUpdateWorkflow(undefined);
    }
  }, [workflowStep.globalController]);

  return(
    <PageView className="biller-groups-page">
      <div className="page-sub-header flex-container flex-horizontal align-center space-between">
        <p className="semi-bold-text flex-width">BILLER GROUPS</p>
        <Link className="set-width flex-container flex-horizontal align-center" to="/payBills">
          <span  data-point="Biller_Groups_Close">Close</span>
          <SVGAsset id="CloseIcon" className="blue-icon" />
        </Link>
      </div>

      <PageLayoutContainer>
        <CenterColumnContainer>
          { deletedBillerGroup &&
          <SuccessNotificationView 
              message={
                <span><b>{ deletedBillerGroupName }</b> has been deleted.</span>
              } 
              showClose={ false } />
          }
          { addedNewBillerGroup &&
          <SuccessNotificationView 
            message={
              <span><b>{ addedNewBillerGroupName }</b> has been successfully added.</span>
            } 
            showClose={ false } />
          }
          { didMoveBillerToBillerGroup &&
          <SuccessNotificationView 
            message={
              <MovedBillerToBillerGroupMessageView biller={ movedBiller} billerGroupFrom={ movedBillerFromBillerGroup } billerGroupTo={ movedBillerToBillerGroup }/>
            } 
            showClose={ false } />
          }
          { didEditBillerGroup &&
          <SuccessNotificationView 
          message={<><b>{ currBillerGroupEditItem.billerGroup.name }</b> has been successfully edited to <b>{ currBillerGroupEditItem.editedBillerGroup.name }.</b></>} 
          showClose={ false } />
          }
          <CreateBillerGroupWidget onChange={ billerGroupNameInputVal } value={ newBillerGroup.name } onAddGroupClick={ onModalAddGroupClick } billerGroup={ newBillerGroup }/>
          <div className="flex-container flex-vertical">
            {/* 
              Existing Biller Group 
            */}
            { billerGroupAccordions }
          </div>
        </CenterColumnContainer>
      </PageLayoutContainer>
    </PageView>
  );
}

function MovedBillerToBillerGroupMessageView(props) {
  const biller = props.biller ?? {};
  const billerGroupFrom = props.billerGroupFrom ?? {};
  const billerGroupTo = props.billerGroupTo ?? {};
  return (
    <span><b>{ biller.name + " " + biller.number }</b> has been moved from <b>{ billerGroupFrom.name ? billerGroupFrom.name : "Other billers" }</b> to <b>{ billerGroupTo.name }</b> successfully.</span>
  );
}

/**
 * @function CreatebillerGroupWidget
 * @returns {React.Component}
 * @exported false
 * @description This is the view that displays "no billers" content.
 * @example
 * <CreateBillerGroupWidget onClick={props.onClick} />
 */
function UserAddedBillerGroupView(props) {
  const billerGroups = props.billerGroups ?? [];
  const billerGroup = props.billerGroup ?? BCMSDefaultModel.billerGroup();
  const billers = props.billers ?? [];
  const billerGroupName = billerGroup.name === "" ? BCMSDefaultModel.billerGroup().name : billerGroup.name;
  const billerGroupEditItems = props.billerGroupEditItems ?? [];
  const billerGroupEditItem = billerGroupEditItems.filter(item => item.billerGroup.id === billerGroup.id)[0];
  const isEditing = billerGroupEditItems.filter(item => item.billerGroup.id === billerGroup.id).length > 0 ? true : false;

  const onBillerGroupItemClick = obj => {
    props.onBillerGroupItemClick(obj)
  }

  var billerViews = [];

  if(billers.length > 0) {
    billerViews = billerGroup.billers.reduce((_acc, biller) => {
      _acc.push( <GroupBillerView key={ biller.id } biller={ biller } billerGroups={ billerGroups } onBillerGroupItemClick={ onBillerGroupItemClick } />);
      return _acc;
    }, []);
  }

  const hideGroup = val => {
    props.hideGroup({billerGroup: billerGroup, hideGroup: val});
  }

  const onDeleteButtonClick = obj => {
    props.onDeleteButtonClick(obj);
  }

  const onConfirmButtonClick = obj => {
    props.onConfirmButtonClick(obj);
  }

  const modalData = { onDeleteButtonClick: onDeleteButtonClick,
  billerGroup: billerGroup,
  billerGroupName: billerGroupName };

  const editModalData = { onPrimaryButtonClick: onConfirmButtonClick,
    billerGroup: billerGroup,
    billerGroupName: billerGroupName,
    billerGroupEditItem: billerGroupEditItem };

  const toggleEditState = () => {
    props.toggleEditBillerGroupState({ billerGroup: billerGroup });
  };

  return (
    <>
    <BillerGroupsButtonContainer>
      <div className="set-width">
        <UIToggle text="Display group" checked={ billerGroup.show } onChange={ hideGroup } dataPoint={ `Groups_Display_Group_${ billerGroup.id }` } />
      </div>
      <div className="flex-width justify-right text-right">
        { isEditing && 
        <div className="flex-container flex-horizontal justify-right">
          <ModalOpen id="EditBillerGroupModal" data={ editModalData }>
            <BillerGroupsButton icon="CheckmarkIcon" text="Confirm" />
          </ModalOpen>
          <BillerGroupsButton className="cancel-button" icon="XIcon" text="Cancel" onClick={ toggleEditState } />
        </div>
        }
        { !isEditing &&
        <div className="flex-container flex-horizontal flex-wrap justify-right">
        <BillerGroupsButton className="edit-button" icon="EditIcon" text="Edit" dataPoint={ `Groups_Edit_Button_${ billerGroup.id }` } onClick={ toggleEditState } />
          {/*<ModalOpen id="EditBillerGroupModal">
            <BillerGroupsButton icon="EditIcon" text="Edit" dataPoint={ `Groups_Edit_Button_${ billerGroup.id }` } />
          </ModalOpen>*/}
          <ModalOpen className="modal-group-delete-button" id="DeleteBillerGroupModal" data={ modalData }>
            <BillerGroupsButton icon="TrashCanIcon" text="Delete" dataPoint={ `Groups_Delete_Button_${ billerGroup.id }` } />
          </ModalOpen>
        </div>
        }
      </div>
    </BillerGroupsButtonContainer>

    {/* Shown when there is no GroupBillerView shown - shows for newly added Biller Group */}
    { billers.length < 1 &&
    <InfoNotificationView
      text={
        <p>There are no billers in this group. Add billers to display this group on the <b>Pay Bills</b> page.</p>
      } />
    }
    
    {/* GroupBillerView are shown when the user moves the biller into the biller group - when these are shown, the InfoNotificationView is not shown. */}
    { billers.length > 0 && billerViews }
    </>
  )
}

/**
 * @function CreatebillerGroupWidget
 * @returns {React.Component}
 * @exported false
 * @description This is the widget only used for the BillerGroupsPage to add a biller group.
 * @example
 * <CreateBillerGroupWidget onClick={props.onClick} />
 */
function CreateBillerGroupWidget(props) {
  const billerGroup = props.billerGroup ?? BCMSDefaultModel.billerGroup();
  const onAddGroupClick = () => props.onAddGroupClick();
  const billerGroupName = billerGroup.name === "" ? BCMSDefaultModel.billerGroup().name : billerGroup.name;
  const modalData = {onAddGroupClick: onAddGroupClick,
  billerGroup: billerGroup,
  billerGroupName: billerGroupName};

  return (
    <div className="create-biller-group-widget flex-container flex-horizontal align-center">
      <div className="flex-width">
        <UITextInput placeHolder="Create biller group" value={ props.value } onChange={ props.onChange } dataPoint="Biller_Groups_Create_Field" />
      </div>
      <div className="set-width">
        <UIButton className="secondary-button" dataPoint="Biller_Groups_Add_Button">
          <ModalOpen id="AddBillerGroupModal" data={ modalData }>Add</ModalOpen>
        </UIButton>
      </div>
    </div>
  );
}